import { useState } from "react";
import {
  BarChart3,
  BookOpen,
  Building2,
  Coins,
  Layout,
  Link2,
  Menu,
  ReceiptText,
  Sparkles,
  User,
  Users,
  UserSearch,
  Wallet,
  X,
} from "lucide-react";
import { navigate, usePath } from "../lib/router";

/**
 * Sticky Bottom-Navigation fuer Agenten auf Mobile (md:hidden).
 * 4 wichtigste Bereiche + "Mehr"-Sheet mit allen restlichen Seiten —
 * wie eine native App.
 */

const ITEMS = [
  { path: "/", icon: Coins, label: "Hub", match: (p: string) => p === "/" || p === "" },
  { path: "/links", icon: Link2, label: "Links", match: (p: string) => p.startsWith("/links") },
  {
    path: "/page-editor",
    icon: Layout,
    label: "Seite",
    match: (p: string) => p.startsWith("/page-editor"),
  },
  {
    path: "/stats",
    icon: BarChart3,
    label: "Stats",
    match: (p: string) => p.startsWith("/stats"),
  },
];

const MORE_ITEMS = [
  { path: "/team", icon: Users, label: "Mein Team" },
  { path: "/customers", icon: UserSearch, label: "Meine Kunden" },
  { path: "/commissions", icon: Coins, label: "Provisionen" },
  { path: "/payouts", icon: Wallet, label: "Auszahlungen" },
  { path: "/rechnungscheck", icon: ReceiptText, label: "Rechnungs-Check" },
  { path: "/prospekte", icon: BookOpen, label: "Online-Prospekt" },
  { path: "/kundenliste", icon: Building2, label: "Kunden-Verzeichnis" },
  { path: "/vorlagen", icon: Sparkles, label: "Sharing-Vorlagen" },
  { path: "/account", icon: User, label: "Stammdaten + Passwort" },
  { path: "/hilfe", icon: BookOpen, label: "Hilfe + Anleitung" },
];

const MORE_PATHS = [
  "/team",
  "/customers",
  "/kunden",
  "/kundenliste",
  "/commissions",
  "/payouts",
  "/rechnungscheck",
  "/prospekte",
  "/prospekt",
  "/vorlagen",
  "/account",
  "/upgrade",
  "/hilfe",
];

export function MobileBottomNav() {
  const path = usePath();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = MORE_PATHS.some((p) => path.startsWith(p));

  function go(target: string) {
    setMoreOpen(false);
    navigate(target);
  }

  return (
    <>
      {moreOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/30"
          onClick={() => setMoreOpen(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 rounded-t-2xl bg-white shadow-2xl p-4 pb-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Alle Bereiche
              </span>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                className="tap-target text-ink-muted hover:text-ink"
                aria-label="Menü schließen"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {MORE_ITEMS.map((item) => {
                const Icon = item.icon;
                const active = path.startsWith(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => go(item.path)}
                    className={`flex items-center gap-2.5 rounded-xl border px-3 py-3 text-left text-sm font-medium transition ${
                      active
                        ? "border-accent-orange/40 bg-orange-50 text-accent-orange"
                        : "border-line bg-white text-ink-soft active:bg-cream-soft"
                    }`}
                  >
                    <Icon className="size-4 flex-shrink-0" />
                    <span className="leading-tight">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <nav
        data-bottom-nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-t border-line shadow-[0_-4px_12px_rgba(0,0,0,0.04)] print:hidden"
      >
        <div className="grid grid-cols-5">
          {ITEMS.map((item) => {
            const Icon = item.icon;
            const active = item.match(path);
            return (
              <button
                key={item.path}
                onClick={() => {
                  setMoreOpen(false);
                  navigate(item.path);
                }}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 tap-target transition ${
                  active ? "text-accent-orange" : "text-ink-muted active:text-ink"
                }`}
                aria-label={item.label}
                aria-current={active ? "page" : undefined}
              >
                <Icon className={active ? "size-5" : "size-5 opacity-80"} strokeWidth={active ? 2.5 : 2} />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className={`flex flex-col items-center justify-center gap-0.5 py-2 tap-target transition ${
              moreActive || moreOpen ? "text-accent-orange" : "text-ink-muted active:text-ink"
            }`}
            aria-label="Mehr"
            aria-expanded={moreOpen}
          >
            <Menu
              className={moreActive || moreOpen ? "size-5" : "size-5 opacity-80"}
              strokeWidth={moreActive || moreOpen ? 2.5 : 2}
            />
            <span className="text-[10px] font-medium">Mehr</span>
          </button>
        </div>
      </nav>
    </>
  );
}
